// app/javascript/components/shared/ThemePicker.tsx
import React, { useState } from 'react'
import { Check, Palette, Moon, Sun } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTheme } from './ThemeProvider'
import { THEME_FAMILIES } from '@/lib/themes'

export function ThemePicker() {
  const { currentTheme, availableThemes, setTheme } = useTheme()
  const [open, setOpen] = useState(false)

  // Deduplicate themes to show unique Theme Families only
  const uniqueFamilies = THEME_FAMILIES.map(family => {
    // Find the variant matching the user's current mode, or fallback to light
    const activeVariantName = currentTheme.mode === 'dark' ? family.darkThemeName : family.lightThemeName
    const activeVariant = availableThemes.find(t => t.name === activeVariantName) || availableThemes.find(t => t.family === family.id)

    return {
      family,
      activeVariant,
      isCurrentlyActiveFamily: currentTheme.family === family.id,
    }
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full flex items-center justify-between gap-2 bg-glass border-glass-border hover:bg-primary/10 text-foreground transition-all"
        >
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold">Change Theme</span>
          </div>
          <Badge variant="secondary" className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5">
            {currentTheme.displayName.replace(' Light', '').replace(' Dark', '')}
          </Badge>
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border/80 shadow-glass-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold text-foreground">
            <Palette className="h-5 w-5 text-primary" /> Select Appearance Theme
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Customize your portal experience with curated color palettes. Toggle mode anytime using the sun/moon button.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 py-3">
          {uniqueFamilies.map(({ family, activeVariant, isCurrentlyActiveFamily }) => {
            if (!activeVariant) return null
            return (
              <button
                key={family.id}
                onClick={() => {
                  setTheme(activeVariant.name)
                  setOpen(false)
                }}
                className={`flex items-center justify-between p-3.5 rounded-xl border transition-all text-left ${
                  isCurrentlyActiveFamily
                    ? 'border-primary bg-primary/15 shadow-glass-sm ring-1 ring-primary/40'
                    : 'border-border/60 bg-card/60 hover:bg-muted/60 hover:border-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center -space-x-1.5 shrink-0">
                    <span
                      className="w-5 h-5 rounded-full border border-black/10 shadow-xs"
                      style={{ backgroundColor: family.swatches[0] }}
                    />
                    <span
                      className="w-5 h-5 rounded-full border border-black/10 shadow-xs"
                      style={{ backgroundColor: family.swatches[1] }}
                    />
                    <span
                      className="w-5 h-5 rounded-full border border-black/10 shadow-xs"
                      style={{ backgroundColor: family.swatches[2] }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground">{family.displayName}</span>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        {activeVariant.mode === 'dark' ? (
                          <Moon className="h-3 w-3 text-secondary" />
                        ) : (
                          <Sun className="h-3 w-3 text-tertiary" />
                        )}
                        <span className="capitalize">{activeVariant.mode} Mode</span>
                      </span>
                    </div>
                  </div>
                </div>

                {isCurrentlyActiveFamily && (
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-fg flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
